import { faBalanceScaleLeft } from "@fortawesome/pro-solid-svg-icons";
import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { RouteComponentProps } from "react-router";

import { Brands } from "@/mk2/apps/strollers/containers/Brands/Brands";
import { strollersBrandsLoad } from "@/mk2/apps/strollers/containers/Brands/Brands.actions";
import reducer from "@/mk2/apps/strollers/containers/Brands/Brands.reducers";
import saga from "@/mk2/apps/strollers/containers/Brands/Brands.sagas";
import { getStrollerBrandsState } from "@/mk2/apps/strollers/containers/Brands/Brands.selectors";
import { strollersComparisonLoad } from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.actions";
import strollersComparisonReducer from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.reducers";
import strollersComparisonSaga from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.sagas";
import { getStrollersComparisonState } from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.selectors";
import { breadcrumbsStrollers } from "@/mk2/apps/strollers/structuredData";
import { strollersComparisonUrl, strollersUrl } from "@/mk2/apps/strollers/urls";
import { Btn, BtnCountType, BtnType } from "@/mk2/components/Btn";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, { HalfpageProps } from "@/mk2/pages/Page";
import { AppState, getRequestBaseUrl } from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { StrollerBrandEntity, StrollerBrandSchema } from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import {
    SEO_TITLE_STROLLERS,
    STROLLEROPEDIA_BRANDS_LIST,
    STROLLERS_COMPARISON,
    STROLLER_SEO_GENERAL_DESC,
} from "@/mk/autogenerated/translation";

import styles from "./Brands.mscss";

interface OwnProps {}

interface StateProps {
    brands: StrollerBrandEntity[];
    hasMore: boolean;
    loadingState: LoadingState;
    errorMessage: string;
    comparison: number[];
    strollersCountPerBrand: Record<number, number>;
    baseUrl: string;
}

interface DispatchProps {
    onLoad(after?: string);
    onStrollersComparisonLoad();
}

type RouteProps = RouteComponentProps<{}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class BrandsPage extends Page<OwnProps & RouteProps, StateProps, DispatchProps> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const brandsState = getStrollerBrandsState(state);
            const strollersComparisonState = getStrollersComparisonState(state);

            return {
                errorMessage: brandsState.errorMessage,
                loadingState: brandsState.loadingState,
                brands: getDenormalizedEntities<StrollerBrandEntity>(
                    state,
                    StrollerBrandSchema,
                    brandsState.brandIds,
                ),
                hasMore: brandsState.hasMore,
                comparison: strollersComparisonState.allIds,
                strollersCountPerBrand: brandsState.strollersCountPerBrand,
                baseUrl: getRequestBaseUrl(state),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: strollersBrandsLoad,
            onStrollersComparisonLoad: strollersComparisonLoad,
        };
    }

    public getMetaTitle(props: Props): string {
        return SEO_TITLE_STROLLERS;
    }

    public getMetaDescription(props: Props): string {
        return STROLLER_SEO_GENERAL_DESC;
    }

    public render(props: Props): false | JSX.Element {
        return <Brands {...props} />;
    }

    public getHeaderTitle(
        props: OwnProps & RouteProps & StateProps & DispatchProps,
    ): string | null {
        return STROLLEROPEDIA_BRANDS_LIST;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        return props.comparison?.length ? (
            <Btn
                className={styles.Brands__leftButton2}
                type={BtnType.BlueOutline}
                link={strollersComparisonUrl}
                label={STROLLERS_COMPARISON}
                count={props.comparison.length}
                countType={BtnCountType.Badge}
                icon={faBalanceScaleLeft}
            />
        ) : null;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(
        props: OwnProps & RouteProps & StateProps & DispatchProps,
    ): string | null {
        return strollersUrl;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Strollers Index - Halfpage Sticky (Desktop)",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.strollers.brands", reducer);
        store.injectSaga("containers.strollers.brands", saga);
        store.injectReducer("containers.strollers.strollersComparison", strollersComparisonReducer);
        store.injectSaga("containers.strollers.strollersComparison", strollersComparisonSaga);
    }

    public getStructuredData(props: Props): object[] {
        return [breadcrumbsStrollers(props.baseUrl)];
    }
}
